/**
 * The bookkeeping store (issue #25) and the three collections it keeps track of.
 *
 * `sync` holds one row per record, outside the record itself, so a Character's schema and its
 * exported file are untouched by signing in. The collections below are the only place the
 * engine learns what this Device holds locally: an id, when it last changed, and its body.
 */
import { listBookmarks, listRecents, applyBookmark, applyBookmarkRemoval, applyRecent, applyRecentRemoval, getBookmark, getRecent } from "../character/personal";
import type { Bookmark, Recent } from "../character/personal";
import { deleteCharacter, getCharacter, listCharacters, STORE_SYNC, complete, request, transaction } from "../character/store";
import type { SyncCollection } from "../../shared/sync-format";
import type { SyncRecord } from "./model";
import { emptyRecord, trackingKey } from "./model";

export async function readRow(collection: SyncCollection, id: string): Promise<SyncRecord | undefined> {
  const tx = await transaction([STORE_SYNC], "readonly");
  return (await request(tx.objectStore(STORE_SYNC).get(trackingKey(collection, id)))) as SyncRecord | undefined;
}

/** The row, or a fresh one that reads as never synced. */
export async function rowOf(collection: SyncCollection, id: string): Promise<SyncRecord> {
  return (await readRow(collection, id)) ?? emptyRecord(collection, id);
}

export async function readRows(collection?: SyncCollection): Promise<SyncRecord[]> {
  const tx = await transaction([STORE_SYNC], "readonly");
  const store = tx.objectStore(STORE_SYNC);
  const rows = (await request(collection ? store.index("collection").getAll(collection) : store.getAll())) as SyncRecord[];
  return rows;
}

export async function writeRows(rows: SyncRecord[]): Promise<void> {
  if (rows.length === 0) return;
  const tx = await transaction([STORE_SYNC], "readwrite");
  const store = tx.objectStore(STORE_SYNC);
  for (const row of rows) store.put(row);
  await complete(tx);
}

export async function writeRow(row: SyncRecord): Promise<void> {
  await writeRows([row]);
}

export async function dropRows(keys: string[]): Promise<void> {
  if (keys.length === 0) return;
  const tx = await transaction([STORE_SYNC], "readwrite");
  const store = tx.objectStore(STORE_SYNC);
  for (const key of keys) store.delete(key);
  await complete(tx);
}

/** Every row, gone. `keep` survives a sign-out: the Conflict Copies this Device still holds. */
export async function clearRows(keep: (row: SyncRecord) => SyncRecord | null): Promise<void> {
  const rows = await readRows();
  const tx = await transaction([STORE_SYNC], "readwrite");
  const store = tx.objectStore(STORE_SYNC);
  store.clear();
  for (const row of rows) {
    const kept = keep(row);
    if (kept) store.put(kept);
  }
  await complete(tx);
}

/** One local record: what the Account is told about it. */
export interface LocalRef {
  id: string;
  /** The record's own `updatedAt`, in milliseconds. */
  updatedAt: number;
}

export interface LocalRecord extends LocalRef {
  data: unknown;
}

/** What the engine can do with one collection's local records. Applying a Character is the engine's own. */
export interface LocalCollection {
  collection: SyncCollection;
  /**
   * Whether a record gone from this Device means it should go from the Account. False for
   * Recents, which fall off the local cap of fifty without being deleted anywhere else.
   */
  inferDeletes: boolean;
  refs(): Promise<LocalRef[]>;
  read(id: string): Promise<LocalRecord | undefined>;
  remove(id: string): Promise<void>;
}

const characters: LocalCollection = {
  collection: "character",
  inferDeletes: true,
  async refs() {
    return (await listCharacters()).map((summary) => ({ id: summary.id, updatedAt: Date.parse(summary.updatedAt) }));
  },
  async read(id) {
    let character;
    try {
      character = await getCharacter(id);
    } catch (e) {
      // A record this build cannot read (one from a newer build, or a damaged one) is left
      // alone: never pushed, never written over, and never mistaken for a deletion, since the
      // summary it still has keeps it out of `refs`'s blind spot.
      console.warn(`sync: Character ${id} could not be read, so it is left out of the sync`, e);
      return undefined;
    }
    return character ? { id, updatedAt: Date.parse(character.updatedAt), data: character } : undefined;
  },
  async remove(id) {
    await deleteCharacter(id);
  },
};

function isBookmark(value: unknown): value is Bookmark {
  const row = value as Partial<Bookmark> | null;
  return typeof row === "object" && row !== null && typeof row.uuid === "string" && typeof row.addedAt === "string" && !Number.isNaN(Date.parse(row.addedAt));
}

function isRecent(value: unknown): value is Recent {
  const row = value as Partial<Recent> | null;
  return typeof row === "object" && row !== null && typeof row.uuid === "string" && typeof row.viewedAt === "string" && !Number.isNaN(Date.parse(row.viewedAt));
}

const bookmarks: LocalCollection = {
  collection: "bookmark",
  inferDeletes: true,
  async refs() {
    return (await listBookmarks()).map((b) => ({ id: b.uuid, updatedAt: Date.parse(b.addedAt) }));
  },
  async read(id) {
    const row = await getBookmark(id);
    return row ? { id, updatedAt: Date.parse(row.addedAt), data: row } : undefined;
  },
  async remove(id) {
    await applyBookmarkRemoval(id);
  },
};

const recents: LocalCollection = {
  collection: "recent",
  inferDeletes: false,
  async refs() {
    return (await listRecents()).map((r) => ({ id: r.uuid, updatedAt: Date.parse(r.viewedAt) }));
  },
  async read(id) {
    const row = await getRecent(id);
    return row ? { id, updatedAt: Date.parse(row.viewedAt), data: row } : undefined;
  },
  async remove(id) {
    await applyRecentRemoval(id);
  },
};

export const COLLECTIONS: Record<SyncCollection, LocalCollection> = { character: characters, bookmark: bookmarks, recent: recents };

/**
 * Write a Bookmark or a Recent another Device sent. Returns the `updatedAt` it was written
 * under, or null when the body is not one this build can read (a newer app's shape).
 */
export async function applyPersonal(collection: "bookmark" | "recent", id: string, data: unknown): Promise<number | null> {
  if (collection === "bookmark") {
    if (!isBookmark(data)) return null;
    const row: Bookmark = { uuid: id, addedAt: data.addedAt };
    await applyBookmark(row);
    return Date.parse(row.addedAt);
  }
  if (!isRecent(data)) return null;
  const row: Recent = { uuid: id, viewedAt: data.viewedAt };
  await applyRecent(row);
  return Date.parse(row.viewedAt);
}
